
import { GoogleGenAI, Type } from "@google/genai";
import { AIContentConfig, TopicStructure, StyleAnalysis } from "../types";
import { SUPREME_EDITORIAL_BRAIN, TITLE_MACHINE_PROMPT, RECIPE_BRAIN, BIBLE_STUDY_BRAIN, IMAGE_DIRECTOR_BRAIN } from "./prompts";

const TEXT_MODEL = 'gemini-3.5-flash'; 
const IMAGE_GENERATOR_MODEL = 'gemini-3.1-flash-image'; 
const TEXT_MODEL_PRO = 'gemini-3.1-pro-preview';

let globalSettings: any = null;

export const setCarouselSettings = (settings: any) => {
    globalSettings = settings;
};

export interface InternalLinkSuggestion {
    keyword: string;
    targetUrl: string;
    targetTitle: string;
}

const getAiClient = (apiKey: string) => {
    const ai = new GoogleGenAI({ apiKey });
    return {
        models: {
            generateContent: async (options: any) => {
                const { model, contents, config } = options;
                
                let prompt = contents;
                if (typeof contents === 'object' && contents.parts) {
                    prompt = contents.parts.map((p: any) => p.text).join('\n');
                } else if (Array.isArray(contents)) {
                    prompt = contents.map((c: any) => {
                        if (typeof c === 'string') return c;
                        if (c.parts) return c.parts.map((p: any) => p.text).join('\n');
                        return "";
                    }).join('\n');
                }

                const result = await ai.models.generateContent({
                    model: model || TEXT_MODEL,
                    contents: prompt,
                    config: {
                        systemInstruction: config?.systemInstruction,
                        responseMimeType: config?.responseMimeType,
                        responseSchema: config?.responseSchema,
                        temperature: config?.temperature !== undefined ? config.temperature : 0.7, // Balanced for creativity and precision
                        topP: config?.topP || 0.95,
                        topK: config?.topK,
                        maxOutputTokens: config?.maxOutputTokens !== undefined ? config.maxOutputTokens : 8192, // Increased for long articles
                        ...(config?.imageConfig ? { imageConfig: config.imageConfig } : {})
                    }
                });

                const textValue = result.text || "";
                
                // Retornar um objeto que satisfaça tanto .text quanto .candidates[0].content.parts[0].text/inlineData
                return { 
                    text: textValue,
                    candidates: [{ 
                        content: { 
                            parts: [
                                { text: textValue },
                                { inlineData: { data: "", mimeType: "" } } // Placeholder para evitar erros de lint/runtime
                            ] 
                        } 

                    }]
                };
            }
        },
        chats: {
            create: (chatOptions: any) => {
                const history = chatOptions.history?.map((h: any) => ({
                    role: h.role === 'user' ? 'user' : 'model',
                    parts: [{ text: h.message || h.parts?.[0]?.text || "" }]
                }));
                const chat = ai.chats.create({
                    model: chatOptions.model || TEXT_MODEL,
                    config: {
                        systemInstruction: chatOptions.config?.systemInstruction,
                    },
                    history: history
                });
                return {
                    sendMessage: async (msg: any) => {
                        const message = typeof msg === 'string' ? msg : (msg.message || msg.parts?.[0]?.text);
                        const result = await chat.sendMessage({ message });
                        const textValue = result.text || "";
                        return { text: textValue };
                    }
                };
            }
        }
    };
};

const callWithKeyRotation = async <T>(apiKeys: string | string[] | undefined, fn: (apiKey: string) => Promise<T>): Promise<T> => {
    let keys = (Array.isArray(apiKeys) ? apiKeys : [apiKeys]).filter(k => !!k);
    if (keys.length === 0) {
        if (globalSettings?.googleApiKey) {
            keys = [globalSettings.googleApiKey, ...(globalSettings.googleBackupKeys || [])].filter(k => !!k);
        }
        
        if (keys.length === 0) {
            if (process.env.GEMINI_API_KEY) {
                keys.push(process.env.GEMINI_API_KEY);
            } else if (process.env.API_KEY) {
                keys.push(process.env.API_KEY);
            } else {
                throw new Error("API Key not found. Configure no painel de configurações.");
            }
        }
    }

    let lastError: any;
    
    for (let i = 0; i < keys.length; i++) {
        const key = keys[i];
        try {
            return await fn(key);
        } catch (error: any) {
            lastError = error;
            const errorMsg = error.message?.toLowerCase() || '';
            const statusCode = error.status || error.statusCode || 0;
            
            console.error(`[Gemini Error Details] Key: ${key.substring(0, 6)}... Status: ${statusCode} Message: ${errorMsg}`, error);
            
            // Fatal errors (Quota exhausted, invalid key, billing issues)
            const isFatalQuotaError = (errorMsg.includes('quota') || 
                                      errorMsg.includes('exhausted') || 
                                      errorMsg.includes('credit') || 
                                      errorMsg.includes('billing') ||
                                      errorMsg.includes('api_key_invalid') ||
                                      errorMsg.includes('permission_denied') ||
                                      statusCode === 403) && statusCode !== 429;
            
            // Retryable errors (Rate limit, server busy)
            const isRetryableError = statusCode === 429 || 
                                     statusCode === 503 || 
                                     statusCode === 500 ||
                                     errorMsg.includes('429') || 
                                     errorMsg.includes('503') || 
                                     errorMsg.includes('too many requests') ||
                                     errorMsg.includes('high demand') ||
                                     errorMsg.includes('unavailable') ||
                                     errorMsg.includes('overloaded');

            if (isFatalQuotaError) {
                console.warn(`[Gemini] Chave #${i+1} de ${keys.length} esgotada ou inválida (${statusCode}): ${errorMsg}. Removendo da rotação atual.`);
                keys.splice(i, 1);
                i--; // Adjust index since we removed an element
                
                if (keys.length === 0) {
                    throw new Error(`Todas as chaves Gemini falharam. Último erro: ${error.message}`);
                }
                continue; // Try next key immediately
            }

            if (isRetryableError) {
                console.warn(`[Gemini] Chave #${i+1} de ${keys.length} ocupada (${statusCode}). Tentando próxima chave...`);
                continue; // Try next key
            }

            // If it's some other error (e.g. 400 Bad Request due to prompt), throw immediately
            throw error;
        }
    }
    
    throw new Error(`Falha após tentar todas as chaves. Último erro: ${lastError?.message}`);
};

// Função utilitária para retry com backoff exponencial (agora simplificada, pois a rotação cuida da maior parte)
const withRetry = async <T>(fn: () => Promise<T>): Promise<T> => {
    // We just call the function. The retry logic is now handled by callWithKeyRotation
    // which will switch keys on 429/503, and loop through all keys multiple times if needed.
    return await fn();
};

const cleanAiHtml = (raw: string): string => {
    // Extract JSON-LD if present before cleaning
    const jsonLdMatch = raw.match(/<script type="application\/ld\+json">[\s\S]*?<\/script>/gi);
    const jsonLd = jsonLdMatch ? jsonLdMatch.join('\n') : '';

    let clean = raw
        .replace(/```html|```/gi, "") 
        .replace(/<!DOCTYPE html>/gi, "")
        .replace(/<html[^>]*>/gi, "")
        .replace(/<\/html>/gi, "")
        .replace(/<head[^>]*>[\s\S]*?<\/head>/gi, "")
        .replace(/<body[^>]*>/gi, "")
        .replace(/<\/body>/gi, "")
        .replace(/<h1[^>]*>[\s\S]*?<\/h1>/gi, "") 
        .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');

    // Strip SEO blocks that AI sometimes outputs at the end
    const seoPatterns = [
        /Meta Title:[\s\S]*?$/i,
        /Meta Description:[\s\S]*?$/i,
        /Slug:[\s\S]*?$/i,
        /Keywords:[\s\S]*?$/i,
        /Palavras-chave:[\s\S]*?$/i,
        /Intenção de busca:[\s\S]*?$/i,
        /Clusters internos:[\s\S]*?$/i,
        /Sugestão de clusters:[\s\S]*?$/i
    ];

    seoPatterns.forEach(pattern => {
        clean = clean.replace(pattern, '');
    });

    clean = clean.trim();

    // Re-inject JSON-LD at the end if it's not already there
    if (jsonLd && !clean.includes('<script type="application/ld+json">')) {
        clean += `\n\n${jsonLd}`;
    }
    
    return clean;
};

const stripTags = (text: string): string => {
    return text
        .replace(/<[^>]*>?/gm, '')
        .replace(/^[0-9.\-\*\s•]+/, '') 
        .replace(/^"|"$/g, '') 
        .replace(/&nbsp;/g, ' ')
        .trim();
};

const buildSystemInstruction = (config?: AIContentConfig, siteContext?: string) => {
    let prompt = SUPREME_EDITORIAL_BRAIN;
    
    // Extrair título e descrição do contexto se possível
    const siteTitle = siteContext?.match(/Site: (.*?) -/)?.[1] || 'este site';
    const siteDescription = siteContext?.match(/- (.*)$/)?.[1] || 'um portal de conteúdo especializado';

    prompt = prompt
        .replace(/{{siteTitle}}/g, siteTitle)
        .replace(/{{siteDescription}}/g, siteDescription);

    return `${prompt}
    CONTEXTO DO SITE:
    ${siteContext || 'Não fornecido'}

    CONFIGURAÇÕES ADICIONAIS DO USUÁRIO:
    - Tom de Voz: ${config?.tone || 'Profissional'}
    - Público Alvo: ${config?.audience || 'Geral'}
    - Instrução Personalizada: ${config?.customPrompt || 'Nenhuma'}
    `;
};

export const generateTopicsStructure = async (theme: string, topicsCount: number, subtopicsCount: number, apiKey?: string | string[], existingTitles: string[] = [], siteContext?: string): Promise<TopicStructure[]> => {
    return await callWithKeyRotation(apiKey, async (currentKey) => {
        const ai = getAiClient(currentKey);
        const avoidList = existingTitles.length > 0 ? `\n\nEVITE ESTES TÍTULOS (JÁ EXISTEM):\n${existingTitles.join('\n')}` : '';
        const prompt = `Como um Arquiteto de Conteúdo SEO, crie a estrutura completa de pauta para o tema: "${theme}". 
        Gere exatamente ${topicsCount} tópicos principais (H2).
        Para CADA tópico principal, gere exatamente ${subtopicsCount} sub-tópicos (H3) detalhados e relevantes.
        Retorne estritamente um JSON seguindo o esquema fornecido.${avoidList}`;
        
        const response = await withRetry(() => ai.models.generateContent({ 
            model: TEXT_MODEL, 
            contents: prompt, 
            config: { 
                systemInstruction: buildSystemInstruction(undefined, siteContext),
                responseMimeType: "application/json",
                responseSchema: {
                    type: Type.OBJECT,
                    properties: {
                        topics: {
                            type: Type.ARRAY,
                            items: {
                                type: Type.OBJECT,
                                properties: {
                                    id: { type: Type.STRING },
                                    title: { type: Type.STRING },
                                    subtopics: {
                                        type: Type.ARRAY,
                                        items: {
                                            type: Type.OBJECT,
                                            properties: {
                                                id: { type: Type.STRING },
                                                text: { type: Type.STRING }
                                            },
                                            required: ["id", "text"]
                                        }
                                    }
                                },
                                required: ["id", "title", "subtopics"]
                            }
                        }
                    },
                    required: ["topics"]
                }
            } 
        }));

        try {
            const parsed = JSON.parse(response.text || "{}");
            return parsed.topics || parsed;
        } catch { return []; }
    });
};

export const generateArticleFromTopics = async (mainTitle: string, category: string, topics: TopicStructure[], config?: AIContentConfig, apiKey?: string | string[], siteContext?: string): Promise<string> => {
    return await callWithKeyRotation(apiKey, async (currentKey) => {
        const ai = getAiClient(currentKey);
        const structure = topics.map(t => `H2: ${t.title}\n${t.subtopics.map(s => `- H3: ${s.text}`).join('\n')}`).join('\n\n');
        const prompt = `Escreva um ARTIGO PILAR PROFUNDO E EXTENSO sobre "${mainTitle}" para "${category}" seguindo a estrutura de tópicos abaixo e as instruções do System Prompt. 
        
        IMPORTANTE: O conteúdo deve ser rico, detalhado e autoritativo (mínimo 2000 palavras), mas deve caber no limite de resposta da IA.
        Siga rigorosamente TODAS as instruções do System Prompt de DIAGRAMAÇÃO EXTREMA, usando <p> para separar parágrafos e OBRIGATORIAMENTE injetando as marcações <div class="ai-summary-box">, <div class="box-destaque"> e <div class="cta-box"> ao longo do HTML.
        Inclua JSON-LD Schema "Article" e "FAQPage" ao final.
        
        ESTRUTURA:\n${structure}`;
        
        const response = await withRetry(() => ai.models.generateContent({ 
            model: TEXT_MODEL, 
            contents: prompt, 
            config: {
                systemInstruction: buildSystemInstruction(config, siteContext)
            }
        }));
        return cleanAiHtml(response.text || "");
    });
};

export const generateTitlesList = async (topic: string, count: number, apiKey?: string | string[], existingTitles: string[] = []): Promise<string[]> => {
    return await callWithKeyRotation(apiKey, async (currentKey) => {
        const ai = getAiClient(currentKey);
        const avoidList = existingTitles.length > 0 ? `\n\n⚠️ PROIBIDO USAR ESTES TÍTULOS (JÁ EXISTEM):\n${existingTitles.join('\n')}` : '';
        const res = await withRetry(() => ai.models.generateContent({ 
            model: TEXT_MODEL, 
            contents: `Gere uma lista estratégica com EXATAMENTE ${count} títulos (pautas) magnéticos e autoritativos sobre "${topic}". Siga rigorosamente as regras de Copywriting e SEO do System Prompt.${avoidList}`,
            config: { systemInstruction: TITLE_MACHINE_PROMPT }
        }));
        return (res.text || "").split('\n').map(t => stripTags(t)).filter(t => t.length > 5);
    });
};

export const generateNewsExcerpt = async (title: string, config?: AIContentConfig, apiKey?: string | string[]): Promise<string> => {
    return await callWithKeyRotation(apiKey, async (currentKey) => {
        const ai = getAiClient(currentKey);
        const res = await withRetry(() => ai.models.generateContent({ 
            model: TEXT_MODEL, 
            contents: `Resumo SEO Master de 30 palavras para a obra: "${title}". Texto puro, sem tags HTML.`,
            config: { systemInstruction: "Você é um Especialista em SEO e Copywriting. Crie resumos curtos, magnéticos e diretos ao ponto." }
        }));
        return stripTags(res.text?.trim() || "");
    });
};

export const generateFullArticle = async (title: string, category: string, config?: AIContentConfig, apiKey?: string | string[], siteContext?: string): Promise<string> => {
    return await callWithKeyRotation(apiKey, async (currentKey) => {
        const ai = getAiClient(currentKey);
        const prompt = `Escreva um ARTIGO PILAR MUITO COMPLETO E DETALHADO sobre: "${title}" na categoria "${category}". Siga rigorosamente todas as instruções do System Prompt para criar o conteúdo mais completo e autoritativo possível.
        
        IMPORTANTE: O conteúdo deve ser rico e detalhado (limitando-se a cerca de 2500 palavras), garantindo que a resposta seja completa e não cortada.
        Siga rigorosamente TODAS as instruções do System Prompt de DIAGRAMAÇÃO EXTREMA, usando <p> para separar parágrafos e OBRIGATORIAMENTE injetando as marcações <div class="ai-summary-box">, <div class="box-destaque"> e <div class="cta-box"> ao longo do HTML.
        Inclua JSON-LD Schema "Article" e "FAQPage" ao final.`;
        
        const res = await withRetry(() => ai.models.generateContent({ 
            model: TEXT_MODEL, 
            contents: prompt,
            config: { systemInstruction: buildSystemInstruction(config, siteContext) }
        }));
        return cleanAiHtml(res.text || "");
    });
};

export const generateAIRecipe = async (title: string, category: string, config?: AIContentConfig, apiKey?: string | string[], siteContext?: string): Promise<any> => {
    return await callWithKeyRotation(apiKey, async (currentKey) => {
        const ai = getAiClient(currentKey);
        const prompt = `Crie uma receita espetacular para: "${title}" na categoria "${category}". Siga rigorosamente todas as instruções do System Prompt para preencher o layout técnico de receitas. IMPORTANTE LIMITAÇÃO DE TAMANHO: Para evitar cortes e JSON quebrado, não gere um conteúdo longo demais (limite máximo razoável de 2000 palavras). Feche o JSON de forma válida.`;
        
        const systemInstruction = `${RECIPE_BRAIN}
        
        CONTEXTO DO SITE:
        ${siteContext || 'Não fornecido'}

        INSTRUÇÕES ADICIONAIS (Cérebro do Post):
        - Tom: ${config?.tone || 'Profissional'}
        - Público: ${config?.audience || 'Geral'}
        - Personalização: ${config?.customPrompt || 'Nenhuma'}
        `;

        const res = await withRetry(() => ai.models.generateContent({ 
            model: TEXT_MODEL, 
            contents: prompt,
            config: { 
                systemInstruction: systemInstruction,
                responseMimeType: "application/json"
            }
        }));
        
        try {
            return JSON.parse(res.text || "{}");
        } catch (e) {
            console.error("Erro ao parsear resposta de receita do Gemini:", e);
            throw new Error("Formato JSON inválido - Resposta cortada ou mal formatada");
        }
    });
};

export const generateImage = async (prompt: string, apiKey?: string | string[], aspectRatio?: string): Promise<string> => {
    const safePrompt = (prompt || "Professional high-quality photography").substring(0, 4000);
    
    // Fallback imediato se o Gemini falhar (comum no free tier)
    const getPollinationsUrl = (p: string) => {
        const seed = Math.floor(Math.random() * 1000000);
        const width = aspectRatio === '16:9' ? 1280 : (aspectRatio === '9:16' ? 720 : 1024);
        const height = aspectRatio === '16:9' ? 720 : (aspectRatio === '9:16' ? 1280 : 1024);
        const enhancedPollPrompt = `Professional photorealistic commercial photography, correct human anatomy, perfect hands: ${p}`;
        return `https://image.pollinations.ai/prompt/${encodeURIComponent(enhancedPollPrompt.substring(0, 500))}?width=${width}&height=${height}&nologo=true&enhance=true&seed=${seed}`;
    };

    return await callWithKeyRotation(apiKey, async (currentKey) => {
        const ai = getAiClient(currentKey);
        
        try {
            console.log(`[Gemini Image] Iniciando geração com chaves qualificadas...`);
            
            // 1. Tenta modelos dedicados de imagem de alta performance (gemini-3.1-flash-image)
            const dedicatedModels = [IMAGE_GENERATOR_MODEL, 'gemini-3.1-flash-lite-image', 'imagen-3.0-generate-001', 'imagen-3', 'imagen-2'];
            for (const modelName of dedicatedModels) {
                try {
                    console.log(`[Gemini Image] Tentando modelo ${modelName}...`);
                    const result = await ai.models.generateContent({
                        model: modelName,
                        contents: { 
                            parts: [{ text: `High-quality photorealistic professional commercial photography, correct human anatomy, perfect natural hands with exactly 5 fingers on each hand, symmetrical face, realistic skin textures, photorealistic: ${safePrompt}` }] 
                        },
                        config: {
                            imageConfig: { 
                                aspectRatio: aspectRatio || "1:1",
                                imageSize: "1K"
                            }
                        }
                    });
                    
                    if (result.candidates?.[0]?.content?.parts) {
                        for (const part of result.candidates[0].content.parts) {
                            if (part.inlineData && part.inlineData.data) {
                                console.log(`[Gemini Image] Sucesso usando o modelo ${modelName}`);
                                return `data:image/png;base64,${part.inlineData.data}`;
                            }
                        }
                    }
                } catch (innerE: any) {
                    console.warn(`[Gemini Image] Falha no modelo específico ${modelName}: ${innerE.message}`);
                }
            }

            // 2. Tenta via modelo de texto (como fallback alternativo se permitido)
            const response = await ai.models.generateContent({
                model: TEXT_MODEL,
                contents: { 
                    parts: [{ text: `Generate a photorealistic, high-quality professional image: ${safePrompt}` }] 
                },
                config: {
                    imageConfig: { aspectRatio: aspectRatio || "1:1" }
                }
            });

            if (response.candidates?.[0]?.content?.parts) {
                for (const part of response.candidates[0].content.parts) {
                    if (part.inlineData && part.inlineData.data) {
                        return `data:image/png;base64,${part.inlineData.data}`;
                    }
                }
            }
        } catch (e: any) {
             console.warn("[Gemini Image] Erro ao usar Gemini para imagem, usando Pollinations como fallback...", e.message);
             return getPollinationsUrl(safePrompt);
        }
        
        // Se nenhum modelo Gemini retornou dados, usa Pollinations
        return getPollinationsUrl(safePrompt);
    });
};

export const enhanceImagePrompt = async (subject: string, styleGuide: string, apiKey?: string | string[]): Promise<string> => {
    try {
        return await callWithKeyRotation(apiKey, async (currentKey) => {
            const ai = getAiClient(currentKey);
            const prompt = `SUBJECT OF THE IMAGE: ${subject}\n\nSTYLE GUIDE & RULES (JSON): ${styleGuide}`;
            
            const res = await withRetry(() => ai.models.generateContent({ 
                model: TEXT_MODEL, 
                contents: prompt,
                config: { systemInstruction: IMAGE_DIRECTOR_BRAIN }
            }));
            
            return res.text?.trim() || subject;
        });
    } catch (e) {
        console.warn("[Gemini Enhance] Falha ao refinar prompt, usando original:", subject);
        return subject;
    }
};

export const generateInternalLinksJSON = async (contentHTML: string, availableTargets: { title: string; url: string; type: 'post' | 'product'; id: string }[], apiKey?: string | string[]): Promise<any[]> => {
    return await callWithKeyRotation(apiKey, async (currentKey) => {
        const ai = getAiClient(currentKey);
        const targets = availableTargets.map(t => `- ID: ${t.id}, Título: "${t.title}" (URL: ${t.url})`).join("\n");
        const prompt = `Analise o texto e sugira links internos JSON. Identifique frases ou palavras-chave no HTML que podem ser usadas como âncora para um link para um dos artigos da LISTA DE ARTIGOS.
        
        REGRAS ESTRITAS:
        1. Retorne APENAS um JSON válido.
        2. O JSON deve ser uma lista de objetos.
        3. Cada objeto deve ter 'keyword' (A FRASE EXATA QUE ESTÁ NO TEXTO HTML), 'artigo_sugerido_id' (o ID do artigo correspondente), 'targetUrl' (a URL do artigo correspondente) e 'targetTitle' (o título do artigo correspondente).
        4. O campo 'keyword' NÃO PODE SER VAZIO.
        5. Não sugira links para o próprio artigo.

        LISTA DE ARTIGOS (para linkar):
        ${targets}
        
        HTML:
        ${contentHTML.substring(0, 4000)}`;
        
        const response = await withRetry(() => ai.models.generateContent({ 
            model: TEXT_MODEL, 
            contents: prompt, 
            config: { 
                systemInstruction: "Você é um Especialista em SEO Técnico e Arquitetura de Informação. Sua tarefa é encontrar as melhores oportunidades de links internos no texto fornecido.",
                responseMimeType: "application/json" 
            } 
        }));
        try { 
            let text = response.text || "[]";
            // Extrair JSON se estiver envolto em blocos de código markdown
            const jsonMatch = text.match(/\[[\s\S]*\]/) || text.match(/\{[\s\S]*\}/);
            if (jsonMatch) text = jsonMatch[0];
            
            const parsed = JSON.parse(text);
            const list = Array.isArray(parsed) ? parsed : (parsed.links || parsed.suggestions || []);
            return list.map((item: any) => ({
                ...item,
                keyword: item.keyword || item.frase || item.anchor || item.text || ''
            })).filter((item: any) => item.keyword && item.targetUrl);
        } catch { return []; }
    });
};

export const generateProductDescription = async (productName: string, apiKey?: string | string[]): Promise<string> => {
    return await callWithKeyRotation(apiKey, async (currentKey) => {
        const ai = getAiClient(currentKey);
        const res = await withRetry(() => ai.models.generateContent({ 
            model: TEXT_MODEL, 
            contents: `Descrição persuasiva (Copywriting Master) de 20 palavras para o sistema: "${productName}". Texto puro, sem tags HTML.`,
            config: { systemInstruction: "Você é um Copywriter Especialista em Vendas. Seja persuasivo e conciso." }
        }));
        return stripTags(res.text?.trim() || "");
    });
};

export const generateMetaDescription = async (title: string, apiKey?: string | string[]): Promise<string> => {
    return await callWithKeyRotation(apiKey, async (currentKey) => {
        const ai = getAiClient(currentKey);
        const res = await withRetry(() => ai.models.generateContent({ 
            model: TEXT_MODEL, 
            contents: `Crie uma meta descrição SEO perfeita, persuasiva e otimizada para o Google (máximo 160 caracteres) para um site com o título: "${title}". A descrição deve ser atraente e incentivar o clique. Retorne apenas o texto da descrição, sem aspas ou explicações.`,
            config: { systemInstruction: "Você é um Especialista em SEO e Copywriting." }
        }));
        return stripTags(res.text?.trim() || "");
    });
};

export const generateViralTitlesByCategory = async (
    category: string, 
    count: number = 10, 
    apiKey?: string | string[], 
    existingTitles: string[] = [],
    brain?: string,
    customTopic?: string
): Promise<string[]> => {
    return await callWithKeyRotation(apiKey, async (currentKey) => {
        const ai = getAiClient(currentKey);
        
        const avoidList = existingTitles.length > 0 ? `\n\n⚠️ PROIBIDO USAR ESTES TÍTULOS (JÁ EXISTEM NO BANCO):\n${existingTitles.join('\n')}` : '';
        
        let brainInstruction = '';
        if (brain) {
            switch (brain) {
                case 'educativo':
                    brainInstruction = `\n🎯 ESTILO DO CÉREBRO: EDUCATIVO / AUTORIDADE\nFoco em ensinar algo novo, decodificar conceitos complexos, trazer fatos de autoridade e educar o público. Faça com que o leitor sinta que vai aprender algo valioso imediatamente. Use ganchos como "Como funciona...", "Guia Completo para...", "O que ninguém te conta sobre...", "A Ciência por trás de...".`;
                    break;
                case 'comparativo':
                    brainInstruction = `\n🎯 ESTILO DO CÉREBRO: COMPARATIVO (ANTES VS DEPOIS / MITO VS VERDADE)\nFoco em contrastar dois cenários polares, mitos vs verdades, ou comparar Produto A vs Produto B. Use ganchos como "[Assunto] vs [Alternativa]: Qual realmente vale a pena?", "Mito ou Verdade sobre...", "O Antes e Depois de...", "Por que o método X supera o Y".`;
                    break;
                case 'tendencias':
                    brainInstruction = `\n🎯 ESTILO DO CÉREBRO: ANÁLISE DE TENDÊNCIAS / ALERTA\nFoco nas novidades do mercado para 2026, atualizações quentes, notícias urgentes e previsões revolucionárias. Toque na curiosidade e no sentimento de novidade. Use ganchos como "A nova tendência de... para 2026", "Alerta: O que muda com...", "Não ignore isso: O futuro do...".`;
                    break;
                case 'cases':
                    brainInstruction = `\n🎯 ESTILO DO CÉREBRO: CASES DE SUCESSO / PROVA SOCIAL\nFoco em histórias da vida real, estudos de caso inspiradores, faturamento, conquistas mensuráveis e prova social incontestável. Use ganchos como "Como conseguiu [Resultado] em poucos dias", "Estudo de caso de sucesso em...", "O que aprendemos com a história real de...".`;
                    break;
                case 'listas':
                    brainInstruction = `\n🎯 ESTILO DO CÉREBRO: LISTAS / GANCHOS MAGNÉTICOS\nFoco em formatos escaneáveis de listas, números ímpares, ferramentas úteis, erros a evitar e segredos revelados. Use ganchos como "7 Erros graves ao...", "11 Ferramentas para...", "5 Dicas práticas de...", "3 Segredos de...".`;
                    break;
                case 'copy':
                    brainInstruction = `\n🎯 ESTILO DO CÉREBRO: COPYWRITING PERSUASIVO & CURIOSIDADE EXTRAORDINÁRIA\nFoco absoluto em micro-narrativas de suspense, curiosidade insaciável, psicologia reversa e o desejo instintivo de abrir loops de mistério. Use ganchos como "O segredo oculto por trás de...", "O que acontece quando você...", "A verdade chocante que nunca te contaram sobre...".`;
                    break;
                case 'vendas':
                    brainInstruction = `\n🎯 ESTILO DO CÉREBRO: ESTRATÉGIA DE VENDA / CONVERSÃO\nFoco comercial direto ou indireto, desejo de compra, bônus fantásticos, quebra de objeções clássicas de vendas, análise de se vale a pena comprar e ofertas vantajosas. Use ganchos como "Vale a pena comprar o...?", "Como economizar com...", "O Guia de Compra Definitivo de...".`;
                    break;
                case 'storytelling':
                    brainInstruction = `\n🎯 ESTILO DO CÉREBRO: HISTÓRIAS / STORYTELLING ENVOLVENTE\nFoco em narrativa rica em emoção humana, vulnerabilidade, jornada heróica, erros, superações pessoais e empatia profunda. Use ganchos como "A história não contada de...", "Como eu quase perdi tudo antes de...", "O dia em que descobri que...".`;
                    break;
                case 'checklists':
                    brainInstruction = `\n🎯 ESTILO DO CÉREBRO: CHECKLISTS / PASSO A PASSO PRÁTICO\nFoco em guias super acionáveis, checklists objetivos, receitas de sucesso diretas e planos simples no estilo passo-a-passo sem rodeios. Use ganchos como "Passo a Passo: Como fazer...", "O checklist definitivo para...", "Cronograma prático para...".`;
                    break;
            }
        }

        const customTopicInstruction = customTopic ? `\n💡 ASSUNTO PARCEIRO / INSTRUÇÕES ADICIONAIS DO USUÁRIO:\nFoque exatamente neste assunto, dor ou instruções que o usuário digitou: "${customTopic}". Mescle este tópico com a categoria de forma extremamente orgânica.` : '';

        const prompt = `
    Você é um especialista global em SEO, Copywriting Persuasivo e Otimização de CTR para Google.
    
    Seu papel é gerar títulos estratégicos altamente clicáveis, baseados na categoria e palavra-chave fornecidas pelo usuário.
    
    Você deve se adaptar automaticamente a qualquer nicho, mercado ou segmento.
    ${avoidList}
    ${brainInstruction}
    ${customTopicInstruction}
    
    🔎 ETAPA 1 – ANÁLISE INTELIGENTE (OBRIGATÓRIA)
    
    Antes de gerar os títulos:
    
    Analise a categoria informada: "${category}"
    ${customTopic ? `Analise também o assunto do usuário: "${customTopic}"` : ''}
    
    Identifique automaticamente:
    - Dores do público
    - Desejos do público
    - Nível de consciência (iniciante, intermediário, avançado)
    - Tipo de linguagem ideal (técnica, comercial, emocional, educativa)
    
    Considere que o conteúdo precisa competir com a primeira página do Google e redes sociais.
    Não exiba essa análise. Apenas use internamente.
    
    🎯 REGRAS OBRIGATÓRIAS PARA OS TÍTULOS
    
    Palavra-chave ou assunto principal deve aparecer de forma visível e atrativa nos títulos.
    Evitar títulos genéricos e sem graça. Retorne ideias magnéticas.
    Evitar clickbait enganoso que não entrega o valor prometido.
    Usar números específicos quando fizer sentido.
    Focar sempre em benefício claro e imediato para o leitor.
    Criar curiosidade legítima.
    Linguagem natural, humana (estilo UGC, recomendação pessoal relevante).
    Máximo de 65 caracteres quando possível.
    Variar estruturas estratégicas e não repetir padrões.
    
    📅 REGRA DE DATA: PROIBIDO usar "2024". Use "2026" ou termos como "Atualizado", "Hoje", "Agora".
    
    Gere exatamente ${count} sugestões de títulos incríveis de acordo com as especificidades do CÉREBRO e do ASSUNTO fornecido acima (caso tenham sido definidos).
    
    REGRAS DE SAÍDA:
    - Retorne APENAS a lista de títulos, um por linha.
    - NÃO use numeração (1., 2., etc) ou marcadores (-).
    - NÃO inclua categorias ou cabeçalhos na saída.
    - NÃO use aspas.
    `;
    
        const res = await withRetry(() => ai.models.generateContent({ 
            model: TEXT_MODEL, 
            contents: prompt,
            config: { systemInstruction: "Você é uma Máquina de Títulos Virais especializada no cérebro selecionado. Retorne apenas texto puro, um título por linha." }
        }));
        return (res.text || "").split('\n').map(l => stripTags(l)).filter(l => l.length > 5);
    });
};

export const generateViralTitles = async (currentTitle: string, apiKey?: string | string[], existingTitles: string[] = []): Promise<string[]> => {
    return await callWithKeyRotation(apiKey, async (currentKey) => {
        const ai = getAiClient(currentKey);
        const avoidList = existingTitles.length > 0 ? `\n\n⚠️ PROIBIDO USAR ESTES TÍTULOS:\n${existingTitles.join('\n')}` : '';
        const res = await withRetry(() => ai.models.generateContent({ 
            model: TEXT_MODEL, 
            contents: `Gere 5 variações de títulos magnéticos e virais nível revista de negócios para: "${currentTitle}". Um por linha, text puro, sem tags HTML e sem aspas. Não use números ou traços no início.${avoidList}`,
            config: { systemInstruction: TITLE_MACHINE_PROMPT }
        }));
        return (res.text || "").split('\n').map(l => stripTags(l)).filter(l => l.length > 5);
    });
};

export const generateLocalSeoArticle = async (service: string, city: string, apiKey?: string | string[], siteContext?: string): Promise<string> => {
    return await callWithKeyRotation(apiKey, async (currentKey) => {
        const ai = getAiClient(currentKey);
        const prompt = `Escreva um ARTIGO PILAR PROFUNDO E EXTENSO (Landing Page de Autoridade) sobre o serviço "${service}" em "${city}". 
        
        IMPORTANTE: O conteúdo deve ser rico e detalhado (mínimo 2000 palavras).
        Gere um JSON-LD Schema "Service" ou "LocalBusiness" específico para "${service}" atendendo em "${city}", incluindo "areaServed" e "hasOfferCatalog". O JSON-LD deve estar no final do conteúdo HTML.
        
        Siga rigorosamente as instruções do System Prompt de DIAGRAMAÇÃO EXTREMA, usando <p> para separar parágrafos e OBRIGATORIAMENTE injetando as marcações <div class="ai-summary-box">, <div class="box-destaque"> e <div class="cta-box"> ao longo do HTML.`;
        const res = await withRetry(() => ai.models.generateContent({ 
            model: TEXT_MODEL, 
            contents: prompt,
            config: { systemInstruction: buildSystemInstruction(undefined, siteContext) }
        }));
        return cleanAiHtml(res.text || "");
    });
};

export const generateLocalAuthorityArticles = async (service: string, city: string, pillarUrl: string, apiKey?: string | string[], siteContext?: string): Promise<{ title: string; content: string }[]> => {
    return await callWithKeyRotation(apiKey, async (currentKey) => {
        const ai = getAiClient(currentKey);
        const prompt = `Como um Especialista em SEO de Autoridade Regional, gere 2 artigos de apoio (Cluster de Autoridade) para o serviço "${service}" na cidade de "${city}".
        
        O objetivo é cercar o tema principal com autoridade semântica e dados locais.
        
        IMPORTANTE: Você DEVE incluir pelo menos um link interno para a página principal (Pillar) usando a URL: ${pillarUrl}. Use âncoras naturais como "serviços de ${service} em ${city}" ou "especialista em ${service}".
        
        Artigo 1: Focado no Panorama Digital/Econômico da cidade (ex: "O Cenário da Transformação Digital em ${city}").
        Artigo 2: Focado em Dores/Dicas específicas para empresas locais (ex: "Como empresas de ${city} podem crescer online com ${service}").
        
        Retorne estritamente um JSON com a chave 'articles' contendo uma lista de objetos com 'title' e 'content' (HTML puro).`;
        
        const response = await withRetry(() => ai.models.generateContent({ 
            model: TEXT_MODEL, 
            contents: prompt,
            config: { 
                systemInstruction: buildSystemInstruction(undefined, siteContext),
                responseMimeType: "application/json",
                responseSchema: {
                    type: Type.OBJECT,
                    properties: {
                        articles: {
                            type: Type.ARRAY,
                            items: {
                                type: Type.OBJECT,
                                properties: {
                                    title: { type: Type.STRING },
                                    content: { type: Type.STRING }
                                },
                                required: ["title", "content"]
                            }
                        }
                    },
                    required: ["articles"]
                }
            }
        }));
        
        try {
            const parsed = JSON.parse(response.text || "{}");
            return (parsed.articles || []).map((a: any) => ({
                title: a.title,
                content: cleanAiHtml(a.content)
            }));
        } catch { return []; }
    });
};

export const generateBusinessData = async (prompt: string, apiKey?: string | string[]): Promise<any> => {
    return await callWithKeyRotation(apiKey, async (currentKey) => {
        const ai = getAiClient(currentKey);
        
        const response = await withRetry(() => ai.models.generateContent({
            model: TEXT_MODEL,
            contents: prompt,
            config: {
                systemInstruction: "Você é um Especialista em Inteligência de Negócios e Google Maps. Sua tarefa é encontrar e extrair informações extremamente detalhadas sobre empresas.",
                responseMimeType: "application/json",
                responseSchema: {
                    type: Type.OBJECT,
                    properties: {
                        name: { type: Type.STRING },
                        description: { type: Type.STRING },
                        category: { type: Type.STRING },
                        address: { type: Type.STRING },
                        city: { type: Type.STRING },
                        phone: { type: Type.STRING },
                        whatsapp: { type: Type.STRING },
                        website: { type: Type.STRING },
                        rating: { type: Type.NUMBER },
                        reviewCount: { type: Type.NUMBER },
                        openingHours: { 
                            type: Type.ARRAY,
                            items: { type: Type.STRING }
                        },
                        googleMapsUrl: { type: Type.STRING },
                        latitude: { type: Type.NUMBER },
                        longitude: { type: Type.NUMBER },
                        imageUrl: { type: Type.STRING },
                        gallery_images: {
                            type: Type.ARRAY,
                            items: { type: Type.STRING }
                        },
                        gallery_videos: {
                            type: Type.ARRAY,
                            items: { type: Type.STRING }
                        },
                        socialLinks: {
                            type: Type.ARRAY,
                            items: {
                                type: Type.OBJECT,
                                properties: {
                                    platform: { type: Type.STRING },
                                    url: { type: Type.STRING }
                                },
                                required: ["platform", "url"]
                            }
                        }
                    },
                    required: ["name", "description", "category", "address", "city"]
                }
            }
        }));

        try {
            return JSON.parse(response.text || "{}");
        } catch (e) {
            console.error("Erro ao parsear resposta de negócio do Gemini:", e);
            return {};
        }
    });
};

export const generateGeneric = async (prompt: string, apiKey?: string | string[]): Promise<string> => {
    return await callWithKeyRotation(apiKey, async (currentKey) => {
        const ai = getAiClient(currentKey);
        const res = await withRetry(() => ai.models.generateContent({ 
            model: TEXT_MODEL, 
            contents: prompt,
            config: { systemInstruction: "Você é um assistente de IA útil e preciso." }
        }));
        return stripTags(res.text?.trim() || "");
    });
};

export const generateChatResponse = async (history: any[], message: string, context: string, apiKey?: string | string[], siteTitle: string = 'este site'): Promise<string> => {
    return await callWithKeyRotation(apiKey, async (currentKey) => {
        const ai = getAiClient(currentKey);
        
        const systemPrompt = `
        VOCÊ É O MENTOR DIGITAL E CONSULTOR MASTER DE ${siteTitle.toUpperCase()}.
        
        IDENTIDADE E ATITUDE:
        - Seja extremamente cordial, prestativo e educado. Use termos como "Com certeza", "É um prazer ajudar" e "Excelente pergunta".
        - Sua missão é guiar o usuário pelas soluções de ${siteTitle}.
        
        O SEGREDO DO SITE:
        - Se perguntarem "como fazer um site assim" ou "como isso foi feito", responda com entusiasmo: 
          "O segredo deste ecossistema é a Automação Inteligente desenvolvida para ${siteTitle}. Ele une o poder da Inteligência Artificial com arquiteturas de alta performance para criar ferramentas que vendem sozinhas. Você pode ter um igual acessando nossa vitrine de [Produtos](/?pagina=produtos) ou solicitando um projeto personalizado em [Contato](/?pagina=contato)."
        
        DIRETRIZES DE RESPOSTA:
        - Use os links fornecidos no contexto abaixo para direcionar o usuário. 
        - Responda apenas o que for relacionado ao nicho de ${siteTitle}, tecnologia, SEO e automação.
        - NUNCA revele segredos técnicos de implementação privada ou dados sensíveis de outros usuários.
        - Se não souber algo, direcione para o WhatsApp de suporte oficial.
        
        CONTEXTO DO SITE:
        ${context}
        `;

        const chat = ai.chats.create({ 
            model: TEXT_MODEL, 
            history: history, 
            config: { 
                systemInstruction: systemPrompt
            } 
        });
        const result = await chat.sendMessage({ message });
        return result.text || "";
    });
};

export const rewriteContent = async (rawText: string, config?: AIContentConfig, apiKey?: string | string[], siteContext?: string): Promise<{ title: string; content: string; excerpt: string }> => {
    return await callWithKeyRotation(apiKey, async (currentKey) => {
        const ai = getAiClient(currentKey);
        
        // 1. Generate Better Title
        const titleRes = await withRetry(() => ai.models.generateContent({
            model: TEXT_MODEL,
            contents: `Analise o texto abaixo e crie UM ÚNICO Título Viral e Magnético (SEO Otimizado) para ele. Siga as regras de Copywriting do System Prompt. Retorne APENAS o texto do título.\n\nTEXTO BASE:\n${rawText.substring(0, 3000)}`,
            config: { systemInstruction: TITLE_MACHINE_PROMPT }
        }));
        // Pega apenas a primeira linha caso a IA ignore e mande várias
        const newTitle = stripTags((titleRes.text || "Sem Título").split('\n')[0]);

        // 2. Generate Content (HTML) - 10k words style
        const contentPrompt = `Atue como um Editor Chefe Sênior. IMPORTANTE: Conclua e finalize a formatação corretamente. Não corte o texto no meio e feche todas as tags HTML. Reescreva o seguinte texto transformando-o em um ARTIGO PILAR MUITO COMPLETO E DETALHADO. Use o texto base apenas como inspiração e fonte de dados, mas expanda e enriqueça (limite máximo de 2500 palavras para evitar corte na resposta e garanta fechamento das tags) com seu conhecimento, seguindo a estrutura e qualidade do System Prompt.\n\nTEXTO BASE:\n"${rawText.substring(0, 5000)}"`;
        
        let contentRes;
        try {
            contentRes = await withRetry(() => ai.models.generateContent({ 
                model: TEXT_MODEL_PRO, 
                contents: contentPrompt, 
                config: { 
                    systemInstruction: buildSystemInstruction(config, siteContext)
                } 
            }));
        } catch (e: any) {
            console.warn("[Gemini] Limite ou erro com gemini-2.5-pro, aplicando fallback automático para gemini-2.5-flash...", e);
            contentRes = await withRetry(() => ai.models.generateContent({ 
                model: TEXT_MODEL, 
                contents: contentPrompt, 
                config: { 
                    systemInstruction: buildSystemInstruction(config, siteContext)
                } 
            }));
        }
        
        const generatedHtml = cleanAiHtml(contentRes.text || "");

        // 3. Generate Excerpt
        const excerpt = await generateNewsExcerpt(newTitle, config, currentKey);

        return {
            title: newTitle,
            content: generatedHtml,
            excerpt: excerpt
        };
    });
};

export const rewriteRssContent = async (title: string, content: string, source: string, config?: AIContentConfig, apiKey?: string | string[], siteContext?: string): Promise<any> => {
    return await callWithKeyRotation(apiKey, async (currentKey) => {
        const ai = getAiClient(currentKey);
        
        // 1. Generate Content (HTML) - Deep and extensive style
        const contentPrompt = `Baseado no seguinte conteúdo de RSS (Fonte: ${source}), escreva um ARTIGO PILAR PROFUNDO E EXTENSO. Título: "${title}". Conteúdo Base: "${content.substring(0, 2000)}". Siga rigorosamente as instruções do System Prompt. Garanta que o conteúdo seja rico e detalhado (limite de 2500 palavras), finalizando a formatação corretamente sem cortar o texto final e fechando todas as tags HTML.`;
        
        let contentRes;
        try {
            contentRes = await withRetry(() => ai.models.generateContent({ 
                model: TEXT_MODEL_PRO, 
                contents: contentPrompt, 
                config: { 
                    systemInstruction: buildSystemInstruction(config, siteContext)
                } 
            }));
        } catch (e: any) {
            console.warn("[Gemini] Limite ou erro com gemini-2.5-pro no RSS, aplicando fallback automático para gemini-2.5-flash...", e);
            contentRes = await withRetry(() => ai.models.generateContent({ 
                model: TEXT_MODEL, 
                contents: contentPrompt, 
                config: { 
                    systemInstruction: buildSystemInstruction(config, siteContext)
                } 
            }));
        }
        
        const generatedHtml = cleanAiHtml(contentRes.text || "");

        // 2. Generate Excerpt
        const excerpt = await generateNewsExcerpt(title, config, currentKey);

        // 3. Generate Better Title
        const titleRes = await withRetry(() => ai.models.generateContent({
            model: TEXT_MODEL,
            contents: `Melhore este título para SEO e viralidade seguindo o System Prompt: "${title}". Apenas o texto.`,
            config: { systemInstruction: TITLE_MACHINE_PROMPT }
        }));
        const newTitle = stripTags((titleRes.text || title).split('\n')[0]);

        return {
            title: newTitle,
            content: generatedHtml,
            excerpt: excerpt
        };
    });
};

export const generateVisualPromptSlogan = async (title: string, context: string, apiKey?: string | string[]): Promise<string> => {
    return await callWithKeyRotation(apiKey, async (currentKey) => {
        const ai = getAiClient(currentKey);
        
        const systemInstruction = `Você é um Diretor de Arte sênior especializado em prompts para DALL-E 3 e Midjourney. Sua missão é transformar títulos e contextos simples em "Scripts de Direção de Arte" realistas.

Siga este blueprint rigoroso para o prompt (NÃO use o título literalmente):
1. Assunto Principal: FOQUE NO REALISMO LITERAL. Crie imagens da vida real: pessoas reais trabalhando em escritórios modernos, reuniões em mesas de reunião, pessoas conversando de forma natural em ambientes profissionais ou casuais. É TERMINANTEMENTE PROIBIDO o uso de metáforas visuais irreais, símbolos de luz brilhante, hologramas, raios saindo de cabeças ou qualquer elemento abstrato/místico. Se o tema for "Inteligência Artificial", mostre alguém usando um computador comum, NÃO mostre robôs ou circuitos brilhantes.
2. Estilo e Humor: 'Cinematic Photorealism, high-end commercial photography, true-to-life, documentary style, sober and professional mood'.
3. Lente e Luz: '85mm lens, f/1.8, bokeh, natural office lighting, daylight, cinematic rim lighting, no unreal glowing effects'.
4. Qualidade Pro: 'extremely detailed skin textures, realistic fabric, 8k resolution, sharp focus'.
5. Restrição Absoluta: SEM TEXTO, SEM LETRAS, SEM LOGOTIPOS. NADA de surrealismo, nada de luzes mágicas neon voadoras ou símbolos flutuantes.
6. Composição: Imagine sempre em um formato vertical/retrato estilo 4x5, centralizando o assunto de forma equilibrada.
7. Anatomia Perfeita: Descreva as mãos de forma natural, com exatamente 5 dedos em cada mão, rostos perfeitamente simétricos e realistas, sem distorções ou membros extras.

IMPORTANTE: O prompt final deve ser em INGLÊS, em um único parágrafo fluido e descritivo.
Termine SEMPRE com este sufixo de qualidade: ", professional commercial photograph, ultra-realistic, natural skin textures, correct human anatomy, perfect natural hands, flawless face, masterpiece, sharp focus, no text, no watermark"`;

        const res = await withRetry(() => ai.models.generateContent({ 
            model: TEXT_MODEL, 
            contents: `ESTE É O TEMA PRINCIPAL DO ARTIGO: "${context}"\nAGORA, CRIE UMA IMAGEM ESPECÍFICA PARA ESTE SUB-TÓPICO (H2): "${title}"\n\nInstrução: Crie um prompt artístico que conecte visualmente o sub-tópico ao tema principal de forma elegante.`,
            config: { systemInstruction }
        }));
        return stripTags(res.text?.trim() || title);
    });
};

export const generateCarouselSlideVisualPrompt = async (title: string, subtitle: string, context: string, apiKey?: string | string[]): Promise<string> => {
    return await callWithKeyRotation(apiKey, async (currentKey) => {
        const ai = getAiClient(currentKey);
        
        const systemInstruction = `Você é um Diretor de Arte sênior especialista em design de carrosséis para redes sociais. 
Sua missão é extrair a essência semântica e a emoção do TÍTULO e da DESCRIÇÃO (subtítulo) de um slide e criar um "Prompt de Direção de Arte" para gerar a imagem perfeita de fundo desse slide em um gerador de IA.

A imagem gerada será usada como BACKGROUND de um slide de carrossel. Portanto, a imagem deve representar EXATAMENTE o conceito do slide, fazendo com que o espectador faça a conexão mental imediata entre o texto lido no slide e a imagem que está vendo no fundo.

Siga este blueprint rigoroso:
1. TRADUÇÃO CONCEITUAL: Analise o título e o subtítulo. Qual é a emoção, a ação ou o objeto principal? Converta isso em uma imagem real e tangível.
2. REALISMO FOTOGRÁFICO: Crie imagens da vida real e de alta qualidade: pessoas, cenários texturizados, ambientes imersivos ou minimalistas que passem a ideia do slide. Evite metáforas bobas ou genéricas (ex: se fala sobre "ideia", não use uma lâmpada brilhante, use uma pessoa pensativa ou anotando algo num quadro de vidro).
3. ESPAÇO PARA TEXTO (Negative Space): Lembre-se que o texto do slide ficará por cima. A imagem deve ter "negative space" (áreas com menos poluição visual).
4. Restrição Absoluta: SEM TEXTO, SEM LETRAS, SEM LOGOTIPOS, SEM ÍCONES.
5. Composição e Luz: 'cinematic lighting, highly detailed, photorealistic, 8k, professional photography, clean composition'.

IMPORTANTE: O prompt final DEVE ser em INGLÊS, em um único parágrafo fluido e descritivo. Não inclua conversas suas, forneça APENAS o prompt em inglês.
Termine SEMPRE com este sufixo de qualidade: ", soft lighting, ample negative space, anatomically correct, symmetrical face, natural skin texture, masterpiece, sharp focus, no text, no letters, no watermark, clean background"`;

        const res = await withRetry(() => ai.models.generateContent({ 
            model: TEXT_MODEL, 
            contents: `TEMA GERAL DO CARROSSEL: "${context}"
TÍTULO DO SLIDE: "${title}"
DESCRIÇÃO (SUBTÍTULO) DO SLIDE: "${subtitle}"

Instrução: Crie O MELHOR PROMPT DE IMAGEM POSSÍVEL, em inglês, que reflita EXATAMENTE a mensagem principal deste slide. A imagem resultante tem que fazer total sentido com o título e a descrição.`,
            config: { systemInstruction }
        }));
        return stripTags(res.text?.trim() || `${title} ${subtitle} concept`);
    });
};

export const generateAudioFromText = async (text: string, apiKey?: string | string[]): Promise<string> => {
    return await callWithKeyRotation(apiKey, async (currentKey) => {
        const ai = getAiClient(currentKey);
        const response = await withRetry(() => ai.models.generateContent({
            model: "gemini-2.5-flash",
            contents: [{ parts: [{ text: text }] }],
            config: {
                speechConfig: {
                    voiceConfig: {
                        prebuiltVoiceConfig: { voiceName: 'Kore' },
                    },
                },
            },
        }));
        
        const base64Audio = response.candidates?.[0]?.content?.parts?.[0]?.inlineData?.data;
        if (!base64Audio) {
            throw new Error("Falha ao gerar áudio.");
        }
        return `data:audio/wav;base64,${base64Audio}`;
    });
};

export const generateReview = async (systemPrompt: string, userPrompt: string, apiKey?: string | string[]): Promise<string> => {
    return await callWithKeyRotation(apiKey, async (currentKey) => {
        const ai = getAiClient(currentKey);
        const response = await withRetry(() => ai.models.generateContent({ 
            model: TEXT_MODEL, 
            contents: userPrompt, 
            config: { 
                systemInstruction: systemPrompt
            } 
        }));
        return response.text || "";
    });
};

export const generateWebStoryJSON = async (title: string, content: string, apiKey?: string | string[]): Promise<any> => {
    return await callWithKeyRotation(apiKey, async (currentKey) => {
        const ai = getAiClient(currentKey);
        const prompt = `Analise o seguinte post de blog e crie um Web Story (Google Web Stories) com 10 slides. 
            Para cada slide, forneça um título curto (máx 50 caracteres) e um texto explicativo e impactante (máx 250 caracteres) que conte uma parte da história.
            Também sugira um prompt de imagem detalhado para cada slide que descreva visualmente o conteúdo de forma cinematográfica.
            
            Título do Post: ${title}
            Conteúdo: ${content.substring(0, 8000)}`;

        const schema = {
            type: Type.OBJECT,
            properties: {
                title: { type: Type.STRING },
                excerpt: { type: Type.STRING },
                slides: {
                    type: Type.ARRAY,
                    items: {
                        type: Type.OBJECT,
                        properties: {
                            title: { type: Type.STRING },
                            content: { type: Type.STRING },
                            imagePrompt: { type: Type.STRING }
                        },
                        required: ["title", "content", "imagePrompt"]
                    }
                }
            },
            required: ["title", "excerpt", "slides"]
        };

        const response = await withRetry(() => ai.models.generateContent({
            model: TEXT_MODEL,
            contents: prompt,
            config: {
                responseMimeType: "application/json",
                responseSchema: schema
            }
        }));

        return JSON.parse(response.text || '{}');
    });
};

export const generateBibleStudyContent = async (title: string, category: string, apiKey?: string | string[], siteContext?: string): Promise<any> => {
    return await callWithKeyRotation(apiKey, async (currentKey) => {
        const ai = getAiClient(currentKey);
        const prompt = `Crie um Estudo Bíblico PROFUNDO, EXTENSO e inspirador para o tema: "${title}" na categoria "${category}".
        
        CONTEXTO DO SITE:
        ${siteContext || 'Não fornecido'}

        Siga rigorosamente as instruções do CÉREBRO DE ESTUDOS BÍBLICOS para gerar o JSON com o conteúdo e os dados técnicos. Por ser um estudo bíblico, o texto em 'content' deve ser BEM COMPLETO, EXAUSTIVO e PROFUNDO, entregando um conteúdo de altíssimo valor teológico.`;

        const response = await withRetry(() => ai.models.generateContent({
            model: TEXT_MODEL,
            contents: prompt,
            config: {
                systemInstruction: BIBLE_STUDY_BRAIN,
                responseMimeType: "application/json"
            }
        }));

        try {
            return JSON.parse(response.text || "{}");
        } catch (e) {
            throw new Error("Formato JSON inválido - Resposta cortada ou mal formatada");
        }
    });
};

export const analyzeReferenceStyle = async (imageBuffer: string, apiKey?: string | string[]): Promise<StyleAnalysis> => {
    return await callWithKeyRotation(apiKey, async (currentKey) => {
        const ai = getAiClient(currentKey);
        const response = await withRetry(() => ai.models.generateContent({
            model: TEXT_MODEL, 
            contents: [
                {
                    parts: [
                        { text: "Analise esta imagem de um carrossel do Instagram e extraia o estilo visual. Retorne APENAS um JSON com: {palette: string[], fonts: string[], mood: string, layout: string}. Palette deve conter cores HEX." },
                        { inlineData: { data: imageBuffer.includes(',') ? imageBuffer.split(',')[1] : imageBuffer, mimeType: "image/png" } }
                    ]
                }
            ],
            config: {
                responseMimeType: "application/json"
            }
        }));
        try {
            return JSON.parse(response.text || "{}");
        } catch {
            return { palette: [], fonts: [], mood: "", layout: "" };
        }
    });
};


export const generateCarouselIdeas = async (niche: string, strategy: string, apiKey?: string | string[]): Promise<string[]> => {
    return await callWithKeyRotation(apiKey, async (currentKey) => {
        const ai = getAiClient(currentKey);
        const prompt = `Como um especialista em marketing de conteúdo e Instagram, me dê exatas 5 ideias de títulos virais e de alta conversão para CARROSSÉIS focados no nicho de "${niche}".
        A estratégia do carrossel deve ser: "${strategy}".
        Retorne um array JSON contendo APENAS as strings com as ideias, no formato: {"ideas": ["Ideia 1", "Ideia 2", ...]}.`;

        const response = await withRetry(() => ai.models.generateContent({
            model: TEXT_MODEL,
            contents: prompt,
            config: {
                responseMimeType: "application/json",
                responseSchema: {
                    type: Type.OBJECT,
                    properties: {
                        ideas: {
                            type: Type.ARRAY,
                            items: { type: Type.STRING }
                        }
                    },
                    required: ["ideas"]
                }
            }
        }));

        try {
            const parsed = JSON.parse(response.text || "{}");
            return parsed.ideas || [];
        } catch { return []; }
    });
};

export const generateCarouselContent = async (theme: string, slideCount: number, strategy?: string, apiKey?: string | string[]): Promise<any[]> => {
    return await callWithKeyRotation(apiKey, async (currentKey) => {
        const ai = getAiClient(currentKey);
        const prompt = `Crie o conteúdo para um carrossel do Instagram com ${slideCount} slides sobre o tema: "${theme}".
        Estratégia: ${strategy || 'Engajamento e Valor'}.
        
        DIRETRIZES DE DESIGN EXATAS (OBRIGATÓRIO):
        - A qualidade do texto deve ser impecável.
        - Os títulos devem obrigatoriamente estar estruturados em quebras de linha usando o caractere "\\n". NÃO corte palavras ao meio ao gerar a quebra de linha.
        - Padrão de Cores do Título (IMPORTANTE: Intercale Branco e Amarelo pelas linhas):
          - Use a marcação ** em toda a linha que deverá ser colorida (amarela). Linhas sem ** ficarão brancas.
          - Se o título tiver DUAS linhas: a primeira DEVE SER BRANCA (sem **), e a segunda DEVE SER AMARELA (com **). Exemplo: "COMO\\n**DESENVOLVER**"
          - Se o título tiver TRÊS ou QUATRO linhas: Alterne as cores. (Exemplo 4 linhas: Branca, Amarela, Branca, Amarela -> "COMO\\n**DESENVOLVER**\\nTRABALHANDO\\n**EM CASA**").
          - Nunca faça um título de uma única linha. Todos os títulos devem ter no mínimo 2 linhas usando "\\n".
        - Os subtítulos (descrições) devem ser SOMENTE EM BRANCO. NÃO USE ** nos subtítulos em hipótese alguma.
        - Títulos devem ser em CAIXA ALTA (MAIÚSCULAS).

        ESTRUTURA DOS SLIDES:
        - Slide 1 (Capa/Destaque): O título deve ser o maior e mais impactante, divida-o em linhas curtas usando "\\n".
        - Slides 2 ao ${slideCount - 1} (Informativos): Desenvolva o conteúdo de forma sequencial e didática.
        - Slide ${slideCount} (Último Slide OBRIGATÓRIO): Deve ser um CALL TO ACTION (CTA) forte e direto, convidando o leitor a interagir (ex: curtir, salvar, comentar, compartilhar ou visitar o link na bio).

        Retorne um JSON com a lista 'slides': { "slides": [{ "title": "string", "subtitle": "string" }] }.`;

        const response = await withRetry(() => ai.models.generateContent({
            model: TEXT_MODEL,
            contents: prompt,
            config: {
                responseMimeType: "application/json",
                responseSchema: {
                    type: Type.OBJECT,
                    properties: {
                        slides: {
                            type: Type.ARRAY,
                            items: {
                                type: Type.OBJECT,
                                properties: {
                                    title: { type: Type.STRING },
                                    subtitle: { type: Type.STRING }
                                },
                                required: ["title", "subtitle"]
                            }
                        }
                    },
                    required: ["slides"]
                }
            }
        }));

        try {
            const parsed = JSON.parse(response.text || "{}");
            const rawSlides = parsed.slides || [];
            return rawSlides.map((s: any) => ({
                ...s,
                title: typeof s.title === 'string' ? s.title.split(/\n/).map((line: string) => line.trim().split(/\s+/).filter(Boolean).join('  ')).join('\n') : ''
            }));
        } catch { return []; }
    });
};

export const generateCTA = async (theme: string, slides: any[], ctaText: string, apiKey?: string | string[]): Promise<any> => {
    return await callWithKeyRotation(apiKey, async (currentKey) => {
        const ai = getAiClient(currentKey);
        const contentContext = slides.map((s, i) => `Slide ${i+1}: ${s.title}`).join('\n');
        const prompt = `Crie um slide de CTA (Call to Action) final para um carrossel sobre "${theme}".
        Contexto dos slides anteriores:\n${contentContext}
        Sugestão do usuário: ${ctaText}
        
        O CTA deve ser extremamente persuasivo e incentivar o comentário, o salvamento ou o clique no link da bio.
        Retorne um JSON: {title: string, subtitle: string}.`;

        const response = await withRetry(() => ai.models.generateContent({
            model: TEXT_MODEL,
            contents: prompt,
            config: {
                responseMimeType: "application/json",
                responseSchema: {
                    type: Type.OBJECT,
                    properties: {
                        title: { type: Type.STRING },
                        subtitle: { type: Type.STRING }
                    },
                    required: ["title", "subtitle"]
                }
            }
        }));

        try {
            const parsed = JSON.parse(response.text || '{"title": "Gostou?", "subtitle": "Siga para mais."}');
            if (parsed && typeof parsed.title === 'string') {
                parsed.title = parsed.title.split(/\n/).map((line: string) => line.trim().split(/\s+/).filter(Boolean).join('  ')).join('\n');
            }
            return parsed;
        } catch { return { title: "Gostou?  Siga  para  mais.", subtitle: "Siga para mais." }; }
    });
};

export const generateInstagramCaption = async (slides: any[], apiKey?: string | string[]): Promise<string> => {
    return await callWithKeyRotation(apiKey, async (currentKey) => {
        const ai = getAiClient(currentKey);
        const contentContext = slides.map((s, i) => `Slide ${i+1}: ${s.title} - ${s.subtitle}`).join('\n');
        const prompt = `Escreva uma legenda matadora para o Instagram para o seguinte carrossel:
        ${contentContext}
        
        A legenda deve ser envolvente, usar emojis, ter um gancho forte no início e terminar com um CTA.
        Inclua hashtags relevantes.`;

        const response = await withRetry(() => ai.models.generateContent({
            model: TEXT_MODEL,
            contents: prompt
        }));
        
        return response.text || "";
    });
};
